import { Marked } from 'marked';
import DOMPurify from 'dompurify';

// The chat-only tool that hands extracted positions to the document form
// (backend ProposePositionsTool). Its call arguments are the proposal.
export const PROPOSE_POSITIONS = 'propose_positions';

export const POSITION_SCAN_PURPOSE = 'position_scan';

const markdown = new Marked({ gfm: true, breaks: true, async: false });
// the description is stored as HTML and printed on the PDF: no pictures or
// scripts from model output
const SANITIZE_OPTIONS = {
  FORBID_TAGS: ['img', 'style', 'form', 'input', 'script', 'iframe'],
};

const toNumber = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : fallback;
};
const round = (value, places) =>
  Math.round(value * 10 ** places) / 10 ** places;

/**
 * A net unit price with a markup on top, like the "Calculation of sales
 * price" module of the positions table computes it (PositionPreCalculation:
 * the markup is rounded to cents and added to the purchase price). A null
 * or non-numeric rate leaves the price as it is.
 */
export function markupPrice(price, rate) {
  const purchase = toNumber(price, 0);
  const percent = toNumber(rate, null);
  if (percent === null) return round(purchase, 2);
  return round(round(purchase * (percent / 100), 2) + purchase, 2);
}

/**
 * A field of a proposed position. The model sends snake_case (the tool's
 * schema), but the API converts every response key to camelCase
 * (ConvertCase middleware), tool call arguments included, so the stored
 * transcript arrives as imageId, isGroup, taxRate, isOptional.
 */
export const positionField = (position, key) =>
  position[key] ??
  position[key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())];

const proposalPositions = (call) =>
  call?.name === PROPOSE_POSITIONS && Array.isArray(call.arguments?.positions)
    ? call.arguments.positions
    : null;

/**
 * The latest propose_positions call of the transcript whose stored result
 * reports success: { callId, messageId, positions }, or null. A streamed
 * call has no result payload yet and a structure error comes back as a
 * successful "Error: …" result, so only the stored transcript counts.
 */
export function latestProposal(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || message.streaming) continue;
    const calls = message.toolCalls || [];
    for (let j = calls.length - 1; j >= 0; j--) {
      const positions = proposalPositions(calls[j]);
      if (!positions || positions.length === 0) continue;
      const result = (message.toolResults || []).find(
        (r) => r.id === calls[j].id,
      );
      if (
        result &&
        !result.failed &&
        !result.denied &&
        typeof result.result === 'string' &&
        !result.result.startsWith('Error:')
      )
        return { callId: calls[j].id, messageId: message.id, positions };
    }
  }
  return null;
}

/** The picture ids a proposal refers to, each once. */
export function proposalImageIds(positions) {
  const ids = new Set();
  (positions || []).forEach((position) => {
    const id = Number(positionField(position, 'image_id'));
    if (Number.isInteger(id) && id > 0) ids.add(id);
  });
  return [...ids];
}

/**
 * The proposed positions in the shape the positions table stores
 * (ExoPositions / handleImportPositions): camelCase keys, the description
 * as sanitized HTML, a percent amount as the fraction the table calculates
 * with, groups without amount and price. `images` maps a picture id to the
 * file the API returned for it; pictures that are not in the map are left off.
 *
 * The document's price is kept as the purchase price of every line, so the
 * sales price can be recalculated later. `markupRates` and
 * `specialMarkupRates` hold a rate in percent per position (same index,
 * null or missing for none); a line with a rate gets it as markupRate /
 * specialMarkupRate and the price with both markups on top.
 */
export function toDocumentPositions(
  positions,
  {
    defaultTaxRate = 19,
    allowOptional = false,
    images = {},
    markupRates = [],
    specialMarkupRates = [],
  } = {},
) {

  return (positions || []).map((position, index) => {
    const field = (key) => positionField(position, key);
    const markup = toNumber(markupRates[index], null);
    const special = toNumber(specialMarkupRates[index], null);
    const hasMarkup = markup !== null || special !== null;
    const isGroup = Boolean(field('is_group'));
    const type = !isGroup && field('type') === 'percent' ? 'percent' : 'default';
    const amount = toNumber(field('amount'), 1);
    const taxRate = toNumber(field('tax_rate'), defaultTaxRate);
    const description = String(field('description') ?? '').trim();
    const image = images[Number(field('image_id'))] || null;
    const isLine = !isGroup && type !== 'percent';
    const purchasePrice = isLine ? round(toNumber(field('price'), 0), 2) : 0;

    return {
      type,
      name: String(field('name') ?? '').trim(),
      description: description
        ? DOMPurify.sanitize(markdown.parse(description), SANITIZE_OPTIONS).trim()
        : null,
      amount: isGroup ? 0 : type === 'percent' ? round(amount / 100, 4) : amount,
      unit: isGroup ? '' : String(field('unit') ?? ''),
      price:
        isLine && hasMarkup
          ? markupPrice(purchasePrice, (markup ?? 0) + (special ?? 0))
          : purchasePrice,
      ...(isLine
        ? {
            purchasePrice,
            ...(markup !== null ? { markupRate: markup } : {}),
            ...(special !== null ? { specialMarkupRate: special } : {}),
          }
        : {}),
      taxRate,
      isGroup,
      isOptional: allowOptional && !isGroup && Boolean(field('is_optional')),
      depth: Math.max(0, parseInt(field('depth'), 10) || 0),
      ...(image ? { image } : {}),
    };
  });
}

/**
 * The index of the group each proposed position belongs to (-1 at the top
 * level), from the depths: a group opens a level for the positions that
 * follow at depth + 1.
 */
export function parentIndexes(positions) {
  const parents = [];
  const open = []; // the groups above the current position: { depth, index }
  (positions || []).forEach((position, index) => {
    const depth = Math.max(0, parseInt(positionField(position, 'depth'), 10) || 0);
    while (open.length && open[open.length - 1].depth >= depth) open.pop();
    parents.push(open.length ? open[open.length - 1].index : -1);
    if (positionField(position, 'is_group')) open.push({ depth, index });
  });
  return parents;
}

/** Every position selected (the default of the review). */
export const allProposed = (positions) =>
  new Set((positions || []).map((_, index) => index));

/**
 * Toggle one position in the selection (a Set of indexes). Deselecting a
 * group deselects what belongs to it; selecting a position selects the
 * groups above it, so what is added keeps a valid structure.
 */
export function toggleProposed(selected, index, positions) {
  const parents = parentIndexes(positions);
  const next = new Set(selected);
  const isBelow = (candidate, ancestor) => {
    for (let k = parents[candidate]; k !== -1; k = parents[k])
      if (k === ancestor) return true;
    return false;
  };

  if (next.has(index)) {
    next.delete(index);
    parents.forEach((_, j) => {
      if (isBelow(j, index)) next.delete(j);
    });
  } else {
    next.add(index);
    for (let k = parents[index]; k !== -1; k = parents[k]) next.add(k);
  }
  return next;
}

/** The selected positions, in order. */
export const selectedProposed = (positions, selected) =>
  (positions || []).filter((_, index) => selected.has(index));
